import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { JSX } from "react";

import { useCan } from "../../../auth/permissions.js";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { toast } from "../../../components/ui/use-toast.js";
import { ApiError } from "../../../lib/api.js";
import { marketingApi } from "../client.js";
import { SectionCardHeader } from "../shared.js";
import { PLATFORM_LABELS } from "../types.js";
import type { ScheduledPost } from "../types.js";
import {
  addMonths,
  dayKey,
  gridRange,
  groupByDayKey,
  localTime,
  monthLabel,
  monthMatrix,
  startOfMonth,
} from "./calendar-utils.js";
import { DayAgenda } from "./schedule-calendar/DayAgenda.js";
import { MonthGrid } from "./schedule-calendar/MonthGrid.js";
import { RescheduleDialog } from "./schedule-calendar/RescheduleDialog.js";

const SCHEDULE_KEY = ["marketing", "schedule"] as const;

/**
 * Schedule calendar — the Workbench's full-width "when does it post" surface.
 * A month grid of scheduled posts built on the already-proxied schedule API:
 * a `from`/`to`-windowed read plus the reschedule (PATCH) + cancel mutations.
 *
 * This file is the orchestrator: the month cursor + day selection, the
 * windowed schedule query, and the reschedule/cancel mutations (cancel is
 * optimistic so it reads as a delete). The month grid lives in
 * `schedule-calendar/MonthGrid`, the selected-day list in `DayAgenda`, the
 * day-cell chip in `CalendarChip`, the move dialog in `RescheduleDialog`, and
 * the date math in `calendar-utils`.
 *
 * Approving a draft schedules it synchronously on the backend (operator-driven,
 * so NOT pause-gated — even a paused agent schedules, it just won't publish) and
 * invalidates `["marketing","schedule"]`, so a freshly-approved post appears here
 * without a manual refresh (our query key is a child of that prefix).
 */
export function ScheduleCalendar(): JSX.Element {
  const canEdit = useCan("marketing:edit");
  const qc = useQueryClient();

  const [cursor, setCursor] = useState<Date>(() => startOfMonth(new Date()));
  const [selectedKey, setSelectedKey] = useState<string>(() => dayKey(new Date()));
  const [rescheduling, setRescheduling] = useState<ScheduledPost | null>(null);
  const [canceling, setCanceling] = useState<ScheduledPost | null>(null);

  const range = gridRange(cursor);
  const calendarKey = [...SCHEDULE_KEY, "calendar", range.fromISO, range.toISO];
  const schedule = useQuery({
    queryKey: calendarKey,
    queryFn: () =>
      marketingApi.listScheduled({ from: range.fromISO, to: range.toISO, limit: 500 }),
    refetchInterval: 30_000,
    retry: 0,
  });

  // Hide terminal non-events. A *canceled* post must vanish from the calendar:
  // the "cancel/delete does nothing" report was the canceled row staying
  // visible (just relabelled "canceled") because we showed every status.
  const rows = useMemo(
    () =>
      (schedule.data ?? []).filter(
        (p) => p.status !== "canceled" && p.status !== "skipped",
      ),
    [schedule.data],
  );
  const byDay = useMemo(() => groupByDayKey(rows), [rows]);
  const weeks = useMemo(() => monthMatrix(cursor), [cursor]);
  const todayKey = dayKey(new Date());
  const cursorMonth = cursor.getMonth();
  const selectedPosts = byDay.get(selectedKey) ?? [];
  const selectedDate = useMemo(() => {
    const [y, m, d] = selectedKey.split("-").map(Number);
    return new Date(y!, m! - 1, d!);
  }, [selectedKey]);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: SCHEDULE_KEY });
  };

  const reschedule = useMutation({
    mutationFn: ({ id, scheduled_for }: { id: number; scheduled_for: string }) =>
      marketingApi.reschedulePost(id, { scheduled_for }),
    onSuccess: () => {
      invalidate();
      setRescheduling(null);
      toast({ title: "Post rescheduled" });
    },
    onError: (e) =>
      toast({
        variant: "destructive",
        title: "Couldn't reschedule",
        description: e instanceof ApiError ? e.message : "Please retry.",
      }),
  });

  // Optimistic: drop the row from the calendar immediately so "cancel" reads
  // as a delete. onSettled reconciles with the server (the refetch returns it
  // as `canceled`, which the `rows` filter then hides — so it stays gone).
  const cancel = useMutation({
    mutationFn: (id: number) => marketingApi.cancelPost(id),
    onMutate: async (id: number) => {
      await qc.cancelQueries({ queryKey: SCHEDULE_KEY });
      const prev = qc.getQueryData<ScheduledPost[]>(calendarKey);
      qc.setQueryData<ScheduledPost[]>(calendarKey, (old) =>
        (old ?? []).filter((p) => p.id !== id),
      );
      return { prev };
    },
    onError: (e, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(calendarKey, ctx.prev);
      toast({
        variant: "destructive",
        title: "Couldn't cancel the post",
        description: e instanceof ApiError ? e.message : "Please retry.",
      });
    },
    onSuccess: () => {
      setCanceling(null);
      toast({ title: "Post canceled" });
    },
    onSettled: () => invalidate(),
  });

  return (
    <Card>
      <SectionCardHeader
        title="Schedule"
        subtitle={
          schedule.isLoading
            ? "Loading…"
            : `${rows.length} scheduled this month · approved drafts post automatically`
        }
        trailing={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              aria-label="Previous month"
              data-testid="cal-prev"
              onClick={() => setCursor((c) => addMonths(c, -1))}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography
              data-testid="cal-month"
              sx={{ fontSize: 13, fontWeight: 600, minWidth: 128, textAlign: "center" }}
            >
              {monthLabel(cursor)}
            </Typography>
            <IconButton
              size="small"
              aria-label="Next month"
              data-testid="cal-next"
              onClick={() => setCursor((c) => addMonths(c, 1))}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              data-testid="cal-today"
              onClick={() => {
                setCursor(startOfMonth(new Date()));
                setSelectedKey(dayKey(new Date()));
              }}
              sx={{ ml: 0.5 }}
            >
              Today
            </Button>
          </Box>
        }
      />
      <CardContent sx={{ px: 2.5, py: 2 }}>
        {schedule.isLoading ? (
          <Skeleton variant="rounded" sx={{ height: 320, width: "100%" }} />
        ) : (
          <Box sx={{ display: "grid", gap: 2 }} data-testid="schedule-calendar">
            {schedule.isError && (
              <Typography sx={{ fontSize: 13, color: "error.main" }}>
                Couldn't load the schedule — showing an empty month. It'll retry
                automatically.
              </Typography>
            )}

            <MonthGrid
              weeks={weeks}
              byDay={byDay}
              cursorMonth={cursorMonth}
              todayKey={todayKey}
              selectedKey={selectedKey}
              onSelectDay={setSelectedKey}
            />

            <DayAgenda
              date={selectedDate}
              posts={selectedPosts}
              canEdit={canEdit}
              onReschedule={setRescheduling}
              onCancel={setCanceling}
            />
          </Box>
        )}
      </CardContent>

      <RescheduleDialog
        post={rescheduling}
        busy={reschedule.isPending}
        onClose={() => setRescheduling(null)}
        onSave={(scheduled_for) =>
          rescheduling && reschedule.mutate({ id: rescheduling.id, scheduled_for })
        }
      />

      <ConfirmDialog
        isOpen={canceling !== null}
        onClose={() => setCanceling(null)}
        title="Cancel this scheduled post?"
        description={
          canceling
            ? `The ${PLATFORM_LABELS[canceling.platform] ?? canceling.platform} post set for ${localTime(canceling.scheduled_for)} won't be published. The draft stays approved — you can re-schedule it later.`
            : undefined
        }
        confirmLabel="Cancel post"
        variant="destructive"
        onConfirm={async () => {
          if (canceling) await cancel.mutateAsync(canceling.id);
        }}
      />
    </Card>
  );
}
